<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitDisabled="submitting || loading"
    :submitTitle="submitTitle"
    :scrollable="false"
    :showDelete="showDelete"
    icon="fa-clipboard-list"
    sizeClass="modal-xl"
    @closed="onClosed"
    @deleted="onDeleted"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="2" v-show="!loading">
      <TabbedBox ref="tabBox">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-item-details">
            <i class="far fa-clipboard-list"></i> Details
          </Tab>
          <Tab tabid="tab-item-services" v-if="isPanelItemType">
            <i class="far fa-tools"></i> Services
          </Tab>
          <Tab tabid="tab-item-activity-log" v-if="!isCreateMode">
            <i class="far fa-history"></i> Activity Log
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-item-details">
            <FormRow label="Item Type" v-if="isCreateMode && itemTypeLookup.length != 1">
              <FormSelect v-show="itemTypeLookup.length != 1" v-model="libraryItemData.type" :options="itemTypeLookup" field="type" placeholder="Item Type" />
              <FormStaticLabel v-show="itemTypeLookup.length == 1">{{formatLookupName(libraryItemData.type, itemTypeLookup)}}</FormStaticLabel>
            </FormRow>
            <FormRow label="Name">
              <FormInputText v-model="libraryItemData.name" placeholder="Name" field="name" />
            </FormRow>
            <FormRow label="Description">
              <FormInputTextArea v-model="libraryItemData.description" placeholder="Description" field="description" />
            </FormRow>
            <FormRow label="Customer Availability">
              <FormMultiSelect v-model="libraryItemData.contacts" :options="customerLookup" placeholder="Select customers" note="Leave blank to be available for all customers" field="contacts" />
            </FormRow>
            <FormRow label="Labels">
              <FormLabelTag popupName="Item Labels" labelType="orderEditorLibraryItem" field="labelList" v-model="libraryItemData.labelList" />
            </FormRow>

            <!-- Panel Item Details -->
            <template v-if="isPanelItemType">
              <FormHorizontalRule />

              <FormRow label="Item Size" :fieldCount="2">
                <template v-slot:field1>
                  <FormInputText type="number" v-model="libraryItemData.itemData.maxSize" placeholder="Max Size" prepend="Max Size" addon="mm" field="maxSize" note="Maximum size of the item. Final item size is adjusted to match image aspect ratio" />
                </template>
                <template v-slot:field2>
                  <FormStaticLabel>
                    Item Size (HxW): <strong>{{libraryItemData.itemData.height}}mm x {{libraryItemData.itemData.width}}mm</strong>
                  </FormStaticLabel>
                </template>
              </FormRow>

              <FormRow label="Display Image" helpTip="Image displayed for the item on the panel. Import PNG files with transparency for best results">
                <FormImageCropper 
                  ref="imageCropper"
                  @imageUpdated="imageCropImageUpdated"
                  @cropStarted="imageCropStarted"
                  @cropUpdated="imageCropUpdated"
                />
              </FormRow>

            </template>
          </TabPage>

          <TabPage tabid="tab-item-services" v-if="isPanelItemType">
            <FormRow label="Associated Service" helpTip="Service inventory stock item. If set, adds this service item for each panel item added to panels">
              <FormInputStockCode :stockType="InventoryStockTypes.glassService" v-model="libraryItemData.stockId" />
            </FormRow>
            <FormHorizontalRule />

            <FormRow label="Apply Default" helpTip="If set, new panels will automatically have this panel item added if the panel meets the filter criteria">
              <FormCheckbox v-model="libraryItemData.applyDefault" field="applyDefault" />
            </FormRow>
            <div v-show="libraryItemData.applyDefault">
              <FormRow label="Default Location" :fieldCount="2">
                <template v-slot:field1>
                  <FormSelect v-model="libraryItemData.defaultAlignHorz" :options="horizontalAlignmentLookup" field="defaultAlignHorz" />
                </template>
                <template v-slot:field2>
                  <FormSelect v-model="libraryItemData.defaultAlignVert" :options="verticalAlignmentLookup" field="defaultAlignVert" />
                </template>
              </FormRow>

              <DataUpdateFilter
                :editing="true"
                label="Price Group Filter"
                v-model="libraryItemData.defaultPriceGroupFilter"
                :options="priceGroupLookup"
                field="defaultPriceGroupFilter"
              />
              <DataUpdateFilter
                :editing="true"
                label="Contact Filter"
                v-model="libraryItemData.defaultCustomerFilter"
                :options="customerLookup"
                field="defaultCustomerFilter"
              />
            </div>
          </TabPage>

          <TabPage tabid="tab-item-activity-log" v-if="!isCreateMode && id">
            <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.orderEditorLibraryItem" :linkId="id">
              <template v-slot:title><h3>Recent Activity</h3></template>
            </ActivityLogStream>
          </TabPage>

        </template>
      </TabbedBox>

    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapState } from "vuex"

import Utils from '@/utils/utils'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import DataUpdateFilter from '@/modules/core/components/dataUpdateFilter'

import { InventoryStockTypes, OrderTypes, LinkTypes } from '@/store/types'
import { LibraryItemTypes, LibraryItemShapeTypes, ShapeTypeLookup, LibraryItemTypeLookup } from '@glass-designer/types'
import { AlignmentTypes, AlignmentTypeLookup } from '@/store/mathTypes'

export default {
  name: 'LibraryItemDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode,
    DataUpdateFilter
  },
  props: {
    isCreateMode: Boolean,
    id: Number,
    typeFilter: Array
  },
  data() {
    return {
      dialogForm: {},
      libraryItemData: {
        type: '',
        itemData: {},
        stockId: null,
        contacts: [],
        applyDefault: false,
        defaultAlignHorz: AlignmentTypes.left,
        defaultAlignVert: AlignmentTypes.bottom,
        
        // For apply defaults filtering
        defaultPriceGroupFilter: {},
        defaultCustomerFilter: {}
      },
      loading: false,
      loadingError: '',
      submitting: false,
      customerLookup: [],
      priceGroupLookup: [],
      selectedImage: {
        updated: false,
        width: 0,
        height: 0
      }
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.InventoryStockTypes = InventoryStockTypes
    this.LibraryItemShapeTypes = LibraryItemShapeTypes
    this.ShapeTypeLookup = ShapeTypeLookup    
  },
  watch: {
    'libraryItemData.itemData.maxSize': {
      handler: function() {
        this.recalculatePanelItemSize()
      }
    }
  },
  mounted() {
    this.loading = true
    this.loadingError = ''

    this.requestData()
    .then(() => {
    })
    .catch(err => {
      this.loadingError = 'Error loading data. Check your internet connection and try again'
    })
    .finally(() => {
      this.loading = false
    })
  },
  computed: {
    title() {
      let itemTypeName = Utils.getLookupName(LibraryItemTypeLookup, this.libraryItemData.type) || 'Item'
      return this.isCreateMode ? `New ${itemTypeName}` : `Update ${itemTypeName}`
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    itemTypeLookup() {
      return LibraryItemTypeLookup.filter(typeLookup => {
        if(!this.typeFilter || this.typeFilter.indexOf(typeLookup.value) != -1)
          return true
        return false        
      })
    },
    showDelete() {
      return !this.isCreateMode && this.userHasDeletePermission(this.PermissionTypes.OrderEditorPermissionTypes.orderEditorLibrary)
    },
    isPanelItemType() {
      return this.libraryItemData.type == LibraryItemTypes.panelItem
    },
    horizontalAlignmentLookup() {
      return AlignmentTypeLookup.filter(lookup => {
        return (
          lookup.value == AlignmentTypes.left || 
          lookup.value == AlignmentTypes.center || 
          lookup.value == AlignmentTypes.right
        )
      })
    },
    verticalAlignmentLookup() {
      return AlignmentTypeLookup.filter(lookup => {
        return (
          lookup.value == AlignmentTypes.top || 
          lookup.value == AlignmentTypes.middle || 
          lookup.value == AlignmentTypes.bottom
        )
      })
    },
    activityStreamURL() {
      return `/ordereditor/libraryitems/${this.id}/activitystream`
    },
  },
  methods: {
    show(item = {}) {
      // Reset form
      this.dialogForm = { }
      this.libraryItemData = {
        type: '',
        name: '',
        description: '',
        labelList: [],
        stockId: null,
        applyDefault: false,
        defaultAlignHorz: AlignmentTypes.left,
        defaultAlignVert: AlignmentTypes.bottom,
        itemData: {
          width: 0,
          height: 0,
          maxSize: 0,
          cutoutList: [],
          shapeList: [],
          assemblyData: {}
        },
        thumbnailImageData: '',
        contacts: [],             // For general availability
        defaultPriceGroupFilter: {},      // For apply defaults filtering
        defaultCustomerFilter: {},        // For apply defaults filtering
      }

      if(this.isCreateMode && item) {
        let itemData = item.itemData || {}
        this.libraryItemData.type = item.type || ''
        this.libraryItemData.itemData.cutoutList = itemData.cutoutList || []
        this.libraryItemData.itemData.centerCutoutList = itemData.centerCutoutList || []
        this.libraryItemData.itemData.shapeList = itemData.shapeList || []
        this.libraryItemData.itemData.assemblyData = itemData.assemblyData || {}
        this.libraryItemData.thumbnailImageData = item.thumbnailImageData || ''
      }

      // Force the type if only 1 type allowed in the filter
      if(this.isCreateMode && this.typeFilter && this.typeFilter.length == 1) {
        this.libraryItemData.type = this.typeFilter[0]

        if(this.libraryItemData.type == LibraryItemTypes.panelItem) {
          this.libraryItemData.itemData.maxSize = 40
          this.libraryItemData.itemData.width = 40
          this.libraryItemData.itemData.height = 40
        }
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      // Cropper image data
      if(this.isPanelItemType) {
        let imageData = this.$refs.imageCropper.getCroppedCanvasPNGUrl({
          minWidth: 16,
          minHeight: 16,
          maxWidth: 512,
          maxHeight: 512,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        })

        this.libraryItemData.displayTextureData = imageData

        // Create smaller version for thumbnail
        let thumbnailData = this.$refs.imageCropper.getCroppedCanvasPNGUrl({
          minWidth: 16,
          minHeight: 16,
          maxWidth: 64,
          maxHeight: 64,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        })
        this.libraryItemData.thumbnailImageData = thumbnailData
      }

      var updateData = {
        type: this.libraryItemData.type,
        name: this.libraryItemData.name,
        description: this.libraryItemData.description,
        labelList: (this.libraryItemData.labelList || []).map(label => label.id),
        stockId: this.libraryItemData.stockId || null,
        itemData: this.libraryItemData.itemData,
        thumbnailImageData: this.libraryItemData.thumbnailImageData,
        displayTextureData: this.libraryItemData.displayTextureData,
        contacts: this.libraryItemData.contacts || []
      }

      if(this.isPanelItemType) {
        updateData.applyDefault = !!this.libraryItemData.applyDefault
        if(this.libraryItemData.applyDefault) {
          updateData.defaultAlignHorz = this.libraryItemData.defaultAlignHorz
          updateData.defaultAlignVert = this.libraryItemData.defaultAlignVert

          updateData.defaultPriceGroupFilter = this.libraryItemData.defaultPriceGroupFilter || {}
          updateData.defaultCustomerFilter = this.libraryItemData.defaultCustomerFilter || {}
        }
      }

      // If the image / crop region was not updated for a panel item, do not update the image data
      // Otherwise it'll continue to get cropped / rescaled & result in blurry images & quality loss
      if(this.isPanelItemType && !this.isCreateMode && !this.selectedImage.updated) {
        delete updateData.thumbnailImageData
        delete updateData.displayTextureData
      }

      this.submitting = true

      if(this.isCreateMode) {
        this.$apiService.glassDesignerService.createLibraryItem(updateData)
        .then(response => {
          this.modalClosed({ id: response.data.id })
          this.hide()
        })
        .catch(err => {
          this.dialogForm = this.processFormResponse(err)
          this.$refs.tabBox.displayErrors()
        })
        .finally(() => {
          this.submitting = false
        })        
      } else {
        this.$apiService.glassDesignerService.updateLibraryItem(this.id, updateData)
        .then(response => {
          this.modalClosed({ id: response.data.id })
          this.hide()
        })
        .catch(err => {
          this.dialogForm = this.processFormResponse(err)
          this.$refs.tabBox.displayErrors()
        })
        .finally(() => {
          this.submitting = false
        })
      }
    },
    onDeleted() {
      this.confirmDeleteMessage('Delete Item', 'Are you sure you want to delete this library item?')
      .then(success => {
        if(success) {
          this.$apiService.glassDesignerService.deleteLibraryItem(this.id)
          .then((resp) => {
            this.successToast(`Item Deleted`)

            this.modalClosed({ deleted: true })
            this.hide()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error', 'Error deleting item')
          })
        }
      })
    },
    requestData() {
      return new Promise(async (resolve, reject) => {
        try {
          if(!this.isCreateMode && this.id) {
            let itemResponse = await this.$apiService.glassDesignerService.getLibraryItem(this.id)
            this.libraryItemData = _.cloneDeep(itemResponse.data)
            this.libraryItemData.itemData = _.cloneDeep(itemResponse.data.itemData || {})
            this.libraryItemData.contacts = (itemResponse.data.contacts || []).map(contact => contact.id)

            // If a panel type, update the image
            if(this.isPanelItemType && this.libraryItemData.displayTextureData) {
              this.$nextTick(() => {
                this.$refs.imageCropper.setImageFromDataURL(this.libraryItemData.displayTextureData)
              })
            }
          }

          let contactListResponse = await this.$apiService.contactService.getContactList({
            isActive: 1,
            isCustomer: 1
          })
          this.customerLookup = contactListResponse.data.map(contact => {
            return { value: contact.id, name: contact.name }
          })

          let priceGroupListResponse = await this.$apiService.pricingService.getStockPriceGroupList()
          this.priceGroupLookup = priceGroupListResponse.data.filter(priceGroup => priceGroup.isActive).map(priceGroup => {
            return { value: priceGroup.id, name: priceGroup.name }
          })

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })
    },

    imageCropStarted(data) {
      this.selectedImage.updated = true
    },
    imageCropImageUpdated() {
      this.selectedImage.updated = true
    },

    imageCropUpdated(data) {
      this.selectedImage.width = parseFloat(data.width || 0)
      this.selectedImage.height = parseFloat(data.height || 0)

      this.recalculatePanelItemSize()
    },
    recalculatePanelItemSize() {
      let maxSize = parseInt(this.libraryItemData.itemData.maxSize || 0)

      var scale = 1.0
      if(this.selectedImage.height > 0)
        scale = Math.max(this.selectedImage.width / this.selectedImage.height, 0.001)

      if(scale >= 1.0) {
        this.libraryItemData.itemData.width = Math.round(maxSize)
        this.libraryItemData.itemData.height = Math.round(maxSize / scale)
      } else {
        this.libraryItemData.itemData.width = Math.round(maxSize * scale)
        this.libraryItemData.itemData.height = Math.round(maxSize)
      }
    }
    
  }
}
</script>